import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "@phosphor-icons/react";
import { Chapter, Reveal } from "@/components/site/Reveal";

const FAQS = [
    {
        q: "How much does artificial turf installation cost in Boise, Idaho?",
        a: "Pricing varies by square footage, terrain, and turf grade. Most Treasure Valley installs land between $9–$15 per square foot installed. We provide free on-site measurements and flat, no-surprise quotes — no hidden fees.",
    },
    {
        q: "Is your artificial turf safe for pets and kids?",
        a: "Absolutely. We exclusively install PFAS-free, lab-tested synthetic grass with antimicrobial fibers and permeable backing. It drains 30+ inches of water per hour, contains no lead or heavy metals, and is rated safe for bare feet and paws.",
    },
    {
        q: "Do you build backyard putting greens in Idaho?",
        a: "Yes. We design and install custom-contoured tournament-grade putting greens with real cups, breaks, and fringe — single hole or full short-game complexes — anywhere across the Treasure Valley.",
    },
    {
        q: "What cities do you serve?",
        a: "We install artificial turf and putting greens across the entire Treasure Valley, Idaho — Boise, Meridian, Nampa, Eagle, Caldwell, Kuna, and Star. Just outside? Reach out anyway — we travel for the right project.",
    },
    {
        q: "How long does artificial turf last?",
        a: "Our turf carries a 16-year manufacturer warranty, and with normal residential use most lawns easily last 15–20 years before showing meaningful wear. Putting greens often last even longer.",
    },
    {
        q: "Will the turf get hot in Idaho summers?",
        a: "We offer cool-tech infill and lighter-pigment turf options engineered to stay 10–15°F cooler than standard synthetic grass on hot Idaho afternoons. A quick rinse cools it instantly.",
    },
];

export const FAQ = () => {
    const [open, setOpen] = useState(0);

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="relative border-t border-brand-border/60 bg-white py-24 md:py-36"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <Chapter
                    index="07"
                    label="FAQ"
                    testid="faq-chapter"
                    title={
                        <>
                            Questions we hear from{" "}
                            <span className="italic text-brand-accent">Idaho homeowners</span>.
                        </>
                    }
                    copy="Don't see what you're looking for? Send us a note via the quote form and we'll get back to you the same day."
                />

                <Reveal>
                    <ul className="divide-y divide-brand-border overflow-hidden rounded-3xl border border-brand-border bg-white">
                        {FAQS.map((f, i) => {
                            const isOpen = open === i;
                            return (
                                <li key={f.q} data-testid={`faq-item-${i}`}>
                                    <button
                                        onClick={() => setOpen(isOpen ? -1 : i)}
                                        data-testid={`faq-toggle-${i}`}
                                        aria-expanded={isOpen}
                                        className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left transition-colors hover:bg-brand-surface md:px-8 md:py-6"
                                    >
                                        <span className="flex items-baseline gap-4">
                                            <span className="font-display hidden text-sm italic text-brand-accent/70 sm:inline">
                                                0{i + 1}
                                            </span>
                                            <span className="font-display text-base font-medium leading-snug text-brand-text md:text-lg">
                                                {f.q}
                                            </span>
                                        </span>
                                        <span
                                            className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-colors ${
                                                isOpen
                                                    ? "border-brand-accent bg-brand-accent/15 text-brand-accent"
                                                    : "border-brand-border bg-brand-surface text-brand-muted"
                                            }`}
                                        >
                                            {isOpen ? (
                                                <Minus size={14} weight="bold" />
                                            ) : (
                                                <Plus size={14} weight="bold" />
                                            )}
                                        </span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                                className="overflow-hidden"
                                            >
                                                <p className="px-6 pb-6 text-sm leading-relaxed text-brand-muted md:px-8 md:pb-7 md:text-base">
                                                    {f.a}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </li>
                            );
                        })}
                    </ul>
                </Reveal>
            </div>
        </section>
    );
};
